// SPDX-License-Identifier: Apache-2.0

// /dev/search — kernel diagnostic bench (was /search). Moved here on 2026-04-26
// so the kernel benches stop masquerading as the product. Page content unchanged
// from the original Search.tsx; only addition is the DevBanner and the relative
// import paths got one extra ../ because routes/ → routes/dev/.
//
// Bench harness for tfidfSpMV (kernel 4) and kHopBFS (kernel 5).
// Mirrors the Notes pattern: button → run kernel → render diagnostic block.
//
// Both kernels here use synthetic random inputs because the storage-layer
// CSR build is being shipped by another agent in parallel.

import { createSignal, Show, For } from "solid-js";
import { createDevice } from "@tether/kernels";
// Direct relative imports because index.ts re-exports for these are landing in
// a follow-up Athena merge; this file owns its own kernel imports for now.
// NOTE: extra ../ vs the original Search.tsx because we live under routes/dev/.
import {
  setupTfidfSpMV,
  uploadPostings,
  runTfidfSpMV,
  disposeTfidfSpMV,
  buildRandomCSR,
} from "../../../../../packages/kernels/tfidfSpMV";
import {
  setupKHopBFS,
  runKHopBFS,
  disposeKHopBFS,
  buildRandomGraph,
} from "../../../../../packages/kernels/kHopBFS";

type TfidfBench = {
  vocabSize: number;
  totalPostings: number;
  nDocs: number;
  qLen: number;
  setupMs: number;
  uploadMs: number;
  runMs: number;
  scoreRange: { min: number; max: number };
  nonzeroCount: number;
  topK: { idx: number; score: number }[];
};

type BfsBench = {
  n: number;
  avgDegree: number;
  source: number;
  k: number;
  setupMs: number;
  runMs: number;
  levels: number[];
  visitedCount: number;
  hopCounts: { hop: number; count: number }[];
  hopOfFirst32: number[];
};

const TFIDF_VOCAB = 5_000;
const TFIDF_POSTINGS = 200_000;
const TFIDF_NDOCS = 50_000;
const TFIDF_Q_LEN = 8;

const BFS_N = 20_000;
const BFS_AVG_DEG = 8;
const BFS_K = 2;

export default function DevSearch() {
  const [error, setError] = createSignal<string | null>(null);
  const [status, setStatus] = createSignal<string>("idle — click a bench to dispatch a real WGSL kernel");
  const [tfidf, setTfidf] = createSignal<TfidfBench | null>(null);
  const [bfs, setBfs] = createSignal<BfsBench | null>(null);
  const [busy, setBusy] = createSignal(false);

  async function runTfidfBench() {
    setError(null);
    setTfidf(null);
    setBusy(true);
    let setup: Awaited<ReturnType<typeof setupTfidfSpMV>> | null = null;
    try {
      setStatus("[tfidf] requesting WebGPU device…");
      const { device } = await createDevice();

      setStatus(`[tfidf] setup @ V=${TFIDF_VOCAB.toLocaleString()}, P=${TFIDF_POSTINGS.toLocaleString()}, N=${TFIDF_NDOCS.toLocaleString()}…`);
      const t0 = performance.now();
      setup = await setupTfidfSpMV(device, TFIDF_VOCAB, TFIDF_POSTINGS, TFIDF_NDOCS, 64);
      const t1 = performance.now();

      setStatus("[tfidf] generating + uploading random CSR posting list…");
      const { termOffsets, postingDoc, postingW } = buildRandomCSR(
        TFIDF_VOCAB, TFIDF_POSTINGS, TFIDF_NDOCS, 42,
      );
      const t2 = performance.now();
      uploadPostings(setup, termOffsets, postingDoc, postingW);
      await device.queue.onSubmittedWorkDone();
      const t3 = performance.now();

      setStatus("[tfidf] generating random query…");
      const queryTerms = new Uint32Array(TFIDF_Q_LEN);
      const queryWeights = new Float32Array(TFIDF_Q_LEN);
      // Deterministic-ish: seed from a known PRNG, but for a bench page Math.random is fine.
      for (let i = 0; i < TFIDF_Q_LEN; i++) {
        queryTerms[i] = Math.floor(Math.random() * TFIDF_VOCAB);
        queryWeights[i] = Math.random();
      }

      setStatus("[tfidf] warmup pass…");
      await runTfidfSpMV(setup, queryTerms, queryWeights);

      setStatus("[tfidf] timed pass…");
      const result = await runTfidfSpMV(setup, queryTerms, queryWeights);

      // Take top 10 by score (CPU sort over the dense N-vector — cheap at N=50k).
      const topPairs: { idx: number; score: number }[] = [];
      for (let i = 0; i < result.scores.length; i++) {
        const s = result.scores[i] ?? 0;
        if (s > 0) topPairs.push({ idx: i, score: s });
      }
      topPairs.sort((a, b) => b.score - a.score);
      const topK = topPairs.slice(0, 10);

      setTfidf({
        vocabSize: TFIDF_VOCAB,
        totalPostings: TFIDF_POSTINGS,
        nDocs: TFIDF_NDOCS,
        qLen: TFIDF_Q_LEN,
        setupMs: t1 - t0,
        uploadMs: t3 - t2,
        runMs: result.totalMs,
        scoreRange: result.scoreRange,
        nonzeroCount: result.nonzeroCount,
        topK,
      });
      setStatus("done");
    } catch (e) {
      setError(String(e instanceof Error ? `${e.message}\n${e.stack ?? ""}` : e));
      setStatus("error");
    } finally {
      if (setup) disposeTfidfSpMV(setup);
      setBusy(false);
    }
  }

  async function runBfsBench() {
    setError(null);
    setBfs(null);
    setBusy(true);
    let setup: Awaited<ReturnType<typeof setupKHopBFS>> | null = null;
    try {
      setStatus("[bfs] requesting WebGPU device…");
      const { device } = await createDevice();

      setStatus(`[bfs] generating random graph N=${BFS_N.toLocaleString()}, avg-deg=${BFS_AVG_DEG}…`);
      const edges = buildRandomGraph(BFS_N, BFS_AVG_DEG, 7);

      setStatus("[bfs] setup (build CSR + buffers)…");
      const t0 = performance.now();
      setup = await setupKHopBFS(device, BFS_N, edges);
      const t1 = performance.now();

      setStatus(`[bfs] BFS from node 0, k=${BFS_K}…`);
      const result = await runKHopBFS(setup, 0, BFS_K);
      const t2 = performance.now();

      // Per-hop histogram from hop_of (level counts also returned separately).
      const hist = new Map<number, number>();
      for (let i = 0; i < result.hopOf.length; i++) {
        const h = result.hopOf[i] ?? 0xffffffff;
        if (h !== 0xffffffff) hist.set(h, (hist.get(h) ?? 0) + 1);
      }
      const hopCounts = Array.from(hist.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([hop, count]) => ({ hop, count }));

      const hopOfFirst32 = Array.from(result.hopOf.slice(0, 32)).map((v) =>
        v === 0xffffffff ? -1 : Number(v),
      );

      setBfs({
        n: BFS_N,
        avgDegree: BFS_AVG_DEG,
        source: 0,
        k: BFS_K,
        setupMs: t1 - t0,
        runMs: t2 - t1,
        levels: result.levels,
        visitedCount: result.visited.length,
        hopCounts,
        hopOfFirst32,
      });
      setStatus("done");
    } catch (e) {
      setError(String(e instanceof Error ? `${e.message}\n${e.stack ?? ""}` : e));
      setStatus("error");
    } finally {
      if (setup) disposeKHopBFS(setup);
      setBusy(false);
    }
  }

  return (
    <>
      <div class="dev-banner">DEV — kernel diagnostic page — internal use</div>
      <div style={{ padding: "32px", "max-width": "880px", margin: "0 auto", "font-family": "system-ui, sans-serif" }}>
        <h1 style={{ margin: "0 0 8px 0", "font-size": "28px", "letter-spacing": "-0.02em" }}>
          search · WGSL bench
        </h1>
        <p style={{ color: "#9a9ab0", "margin-top": 0 }}>
          kernels 4 + 5 of 5 from <code>kernels/MIGRATION.md</code>.
          Sparse posting SpMV (q16.16 atomic) for keyword search, and frontier-based
          BFS (atomicOr visited bitmap) for ego graphs. Random synthetic inputs while
          the storage-layer CSR build lands in parallel.
        </p>

        <p style={{ color: "#fde047", "font-size": "13px", "margin-top": "8px" }}>
          heads-up · per <code>vault-graph-queries.ts:4–7</code>, the CPU BFS wins for
          graphs under ~10k nodes — the per-level submit overhead dominates.
          Use this kernel only for big vaults (&gt;50k nodes) or multi-source BFS.
        </p>

        <div style={{ display: "flex", gap: "8px", margin: "24px 0", "flex-wrap": "wrap" }}>
          <button
            data-testid="tfidf-bench"
            onClick={runTfidfBench}
            disabled={busy()}
            style={btnTfidf}
          >
            tfidfSpMV bench (V=5k, P=200k, N=50k)
          </button>
          <button
            data-testid="bfs-bench"
            onClick={runBfsBench}
            disabled={busy()}
            style={btnBfs}
          >
            kHopBFS bench (N=20k, k=2)
          </button>
        </div>

        <div style={{ "font-size": "14px", color: "#d8d8e8" }}>
          status: <code style={code}>{status()}</code>
        </div>

        <Show when={error()}>
          <pre style={{
            color: "#fca5a5",
            padding: "16px",
            background: "rgba(252,165,165,0.08)",
            border: "1px solid rgba(252,165,165,0.3)",
            "border-radius": "8px",
            margin: "16px 0",
            "white-space": "pre-wrap",
            "font-size": "13px",
          }}>{error()}</pre>
        </Show>

        <Show when={tfidf()}>
          {(b) => (
            <div
              data-testid="tfidf-result"
              style={{
                "margin-top": "32px",
                padding: "20px",
                background: "rgba(167,139,250,0.06)",
                border: "1px solid rgba(167,139,250,0.2)",
                "border-radius": "12px",
              }}
            >
              <h2 style={{ margin: "0 0 16px 0", "font-size": "20px" }}>
                tfidfSpMV · V={b().vocabSize.toLocaleString()} · P={b().totalPostings.toLocaleString()} ·
                N={b().nDocs.toLocaleString()} · Q={b().qLen}
              </h2>
              <table style={{ "border-collapse": "collapse", "font-size": "14px" }}>
                <tbody>
                  <tr><td style={cell}>setup (pipelines + buffers)</td><td style={cellNum}><code>{b().setupMs.toFixed(2)} ms</code></td></tr>
                  <tr><td style={cell}>upload CSR to GPU</td><td style={cellNum}><code>{b().uploadMs.toFixed(2)} ms</code></td></tr>
                  <tr>
                    <td style={cell}>tfidf [scatter + postpass + readback]</td>
                    <td style={cellNum}><code style={{ color: "#86efac" }}>{b().runMs.toFixed(2)} ms</code></td>
                  </tr>
                  <tr>
                    <td style={cell}>score range</td>
                    <td style={cellNum} data-testid="tfidf-score-range">
                      <code>[{b().scoreRange.min.toFixed(4)}, {b().scoreRange.max.toFixed(4)}]</code>
                    </td>
                  </tr>
                  <tr>
                    <td style={cell}>nonzero docs</td>
                    <td style={cellNum} data-testid="tfidf-nonzero-count">
                      <code style={{ color: b().nonzeroCount > 0 ? "#86efac" : "#fca5a5" }}>
                        {b().nonzeroCount.toLocaleString()} / {b().nDocs.toLocaleString()}
                      </code>
                    </td>
                  </tr>
                </tbody>
              </table>
              <h3 style={{ "margin-top": "24px", "font-size": "14px", color: "#9a9ab0", "font-weight": 500 }}>
                top-10 docs by score:
              </h3>
              <ul data-testid="tfidf-topk" style={{ "padding-left": "20px", "font-size": "13px", color: "#d8d8e8" }}>
                <For each={b().topK}>
                  {(m) => <li><code>doc {m.idx.toLocaleString()}: score = {m.score.toFixed(6)}</code></li>}
                </For>
              </ul>
              <p style={{ "margin-top": "16px", "font-size": "12px", color: "#6a6a80" }}>
                q16.16 fixed-point precision: each contribution loses ~5e-6.
                For TF-IDF that's noise; do NOT use compensated summation.
              </p>
            </div>
          )}
        </Show>

        <Show when={bfs()}>
          {(b) => (
            <div
              data-testid="bfs-result"
              style={{
                "margin-top": "32px",
                padding: "20px",
                background: "rgba(110,231,183,0.06)",
                border: "1px solid rgba(110,231,183,0.2)",
                "border-radius": "12px",
              }}
            >
              <h2 style={{ margin: "0 0 16px 0", "font-size": "20px" }}>
                kHopBFS · N={b().n.toLocaleString()} · avg-deg={b().avgDegree} ·
                source={b().source} · k={b().k}
              </h2>
              <table style={{ "border-collapse": "collapse", "font-size": "14px" }}>
                <tbody>
                  <tr><td style={cell}>setup (CSR build + buffers)</td><td style={cellNum}><code>{b().setupMs.toFixed(2)} ms</code></td></tr>
                  <tr>
                    <td style={cell}>BFS [k levels + readback]</td>
                    <td style={cellNum}><code style={{ color: "#86efac" }}>{b().runMs.toFixed(2)} ms</code></td>
                  </tr>
                  <tr>
                    <td style={cell}>visited count</td>
                    <td style={cellNum} data-testid="bfs-visited-count">
                      <code>{b().visitedCount.toLocaleString()}</code>
                    </td>
                  </tr>
                  <tr>
                    <td style={cell}>frontier sizes per level</td>
                    <td style={cellNum} data-testid="bfs-levels">
                      <code>[{b().levels.join(", ")}]</code>
                    </td>
                  </tr>
                </tbody>
              </table>
              <h3 style={{ "margin-top": "24px", "font-size": "14px", color: "#9a9ab0", "font-weight": 500 }}>
                hop histogram:
              </h3>
              <ul data-testid="bfs-hop-counts" style={{ "padding-left": "20px", "font-size": "13px", color: "#d8d8e8" }}>
                <For each={b().hopCounts}>
                  {(h) => <li><code>hop {h.hop}: {h.count.toLocaleString()} nodes</code></li>}
                </For>
              </ul>
              <p style={{ "margin-top": "16px", "font-size": "12px", color: "#6a6a80" }}>
                level-sync BFS tolerates the visited-bit race (atomicOr returns prior value;
                loser silently drops the duplicate enqueue). hop_of[i] = -1 means unvisited.
              </p>
              <details style={{ "margin-top": "12px" }}>
                <summary style={{ cursor: "pointer", color: "#9a9ab0", "font-size": "12px" }}>
                  hop_of for first 32 nodes
                </summary>
                <code style={{ "font-size": "11px", color: "#6a6a80" }}>
                  [{b().hopOfFirst32.join(", ")}]
                </code>
              </details>
            </div>
          )}
        </Show>

        <p style={{ "margin-top": "32px", "font-size": "12px", color: "#6a6a80" }}>
          tfidfSpMV: WebGPU has no <code>atomicAdd&lt;f32&gt;</code> on storage. We quantize each
          contribution to q16.16 (×65536), atomicAdd into a u32 buffer, postpass divides
          back to f32. Precision loss ~5e-6 per add — noise for TF-IDF.
          kHopBFS: one dispatch per level, host reads back the frontier counter between
          levels. For k≤4 the readback is plenty; bigger k → switch to indirect dispatch.
        </p>
      </div>
    </>
  );
}

const btnTfidf = {
  padding: "10px 18px",
  background: "rgba(167,139,250,0.12)",
  border: "1px solid rgba(167,139,250,0.4)",
  color: "#e8d8ff",
  "border-radius": "8px",
  cursor: "pointer",
  "font-size": "14px",
  "font-family": "inherit",
} as const;

const btnBfs = {
  ...btnTfidf,
  background: "rgba(110,231,183,0.12)",
  border: "1px solid rgba(110,231,183,0.4)",
  color: "#bbf7d0",
} as const;

const code = {
  padding: "2px 6px",
  background: "rgba(255,255,255,0.06)",
  "border-radius": "4px",
  "font-family": "ui-monospace, SF Mono, monospace",
} as const;

const cell = { padding: "6px 16px 6px 0", color: "#9a9ab0" } as const;
const cellNum = { padding: "6px 0", "font-variant-numeric": "tabular-nums" } as const;
